import { inject, Injectable } from "@angular/core";
import { SideBarMenuItem } from "../../../../../models/ui/sideBar";
import { PermissionService } from "../../../../../services/api/permission/permission.service";

@Injectable()
export class SideBarBuilderService {
  private sideBarItems: SideBarMenuItem[] = [];
  private permissions = inject(PermissionService);
  private allPossibleSideBarItems = [
    "home",
    "tenants",
    "device_management",
    "gate_systems",
    "butler",
    "customers",
    "map",
    "energy_tool",
    "security_tool",
    "system_alert",
    "orders",
    "fault",
    "roles",
    "notifications",
  ];

  /**
   * Builds the sidebar with all items that the user has permission for
   * @returns the sidebar items
   */
  public buildSideBar(): SideBarMenuItem[] {
    this.sideBarItems = [];
    for (const potentialItem of this.allPossibleSideBarItems) {
      const potentialSidebarItem = this.buildMenuItem(potentialItem);
      if (potentialSidebarItem) {
        this.sideBarItems.push(potentialSidebarItem);
      }
    }
    return this.sideBarItems;
  }

  /**
   * Builds a single sidebar item
   * @param menuItem the name of the item to build
   * @returns the built item or undefined if the user has no permission
   */
  private buildMenuItem(menuItem: string): SideBarMenuItem | undefined {
    switch (menuItem) {
      case "home":
        return this.buildHome();
      case "tenants":
        return this.buildTenants();
      case "device_management":
        return this.buildDeviceManagement();
      case "gate_systems":
        return this.buildGateSystems();
      case "butler":
        return this.buildButler();
      case "roles":
        return this.buildRoles();
      case "customers":
        return this.buildCustomers();
      case "energy_tool":
        return this.buildEnergyTool();
      case "security_tool":
        return this.buildSecurityTool();
      case "system_alert":
        return this.buildSystemAlert();
      case "orders":
        return this.buildOrders();
      case "fault":
        return this.buildFault();
      case "notifications":
        return this.buildNotifications();
      case "map":
        return this.buildMap();
      default:
        return;
    }
  }

  private buildDeviceManagement(): SideBarMenuItem | undefined {
    if (this.permissions.hasPermission(this.permissions.allPermissions.DEVICE_READ)) {
      return {
        name: "side_bar.device_management",
        icon: "device_management",
        path: "deviceManagement",
      };
    } else {
      return;
    }
  }

  /**
   * Builds the home item
   * @returns the built item or undefined if the user has no permission
   */
  private buildHome(): SideBarMenuItem | undefined {
    if (this.permissions.hasPermission(this.permissions.allPermissions.DASHBOARD_READ)) {
      return {
        name: "side_bar.home",
        icon: "home",
        path: "home",
      };
    } else {
      return;
    }
  }

  /**
   * Builds the butler item
   * @returns the built item or undefined if the user has no permission
   */
  private buildButler(): SideBarMenuItem | undefined {
    if (this.permissions.hasPermission(this.permissions.allPermissions.BUTLER_MAINT_CREATE)) {
      return {
        name: "side_bar.butler",
        icon: "butler",
        path: "butler",
      };
    } else {
      return;
    }
  }

  /**
   * Builds the roles item
   * @returns the built item or undefined if the user has no permission
   */
  private buildRoles(): SideBarMenuItem | undefined {
    const roles: SideBarMenuItem = {
      name: "side_bar.roles",
      icon: "user_settings",
      path: "roles",
    };

    if (
      this.permissions.hasPermission(this.permissions.allPermissions.ROLE_CREATE) &&
      this.permissions.hasPermission(this.permissions.allPermissions.ROLE_READ)
    ) {
      return roles;
    } else {
      return;
    }
  }

  /**
   * Builds the tenants item
   * @returns the built item or undefined if the user has no permission
   */
  private buildTenants(): SideBarMenuItem | undefined {
    const tenants: SideBarMenuItem = {
      name: "side_bar.tenants",
      icon: "customer",
      path: "tenants",
    };

    if (
      this.permissions.hasPermission(this.permissions.allPermissions.TENANT_CREATE) &&
      this.permissions.hasPermission(this.permissions.allPermissions.TENANT_READ)
    ) {
      return tenants;
    } else {
      return;
    }
  }

  /**
   * Builds the energy tool item
   * @returns the built item or undefined if the user has no permission
   */
  private buildEnergyTool(): SideBarMenuItem | undefined {
    const energyTool: SideBarMenuItem = {
      name: "side_bar.energy_tool",
      icon: "energy_tool",
      path: "energyTool",
    };

    if (this.permissions.hasPermission(this.permissions.allPermissions.ENERGY_TOOL_READ)) {
      return energyTool;
    } else {
      return;
    }
  }

  /**
   * Builds the security tool item
   * @returns the built item or undefined if the user has no permission
   */
  private buildSecurityTool(): SideBarMenuItem | undefined {
    const securityTool: SideBarMenuItem = {
      name: "side_bar.security_tool",
      icon: "thief_protection",
      path: "securityTool",
    };

    if (this.permissions.hasPermission(this.permissions.allPermissions.THIEF_PROTECTION_STATUS_READ)) {
      return securityTool;
    } else {
      return;
    }
  }

  /**
   * Builds the system alert item
   * @returns the built item or undefined if the user has no permission
   */
  private buildSystemAlert(): SideBarMenuItem | undefined {
    const systemAlert: SideBarMenuItem = {
      name: "side_bar.system_alert",
      icon: "info-circle",
      path: "systemAlerts",
    };

    if (this.permissions.hasPermission(this.permissions.allPermissions.SYSTEM_ALERT_CREATE)) {
      return systemAlert;
    } else {
      return;
    }
  }

  /**
   * Builds the customers item
   * @returns the built item or undefined if the user has no permission
   */
  private buildCustomers(): SideBarMenuItem | undefined {
    const customers: SideBarMenuItem = {
      name: "side_bar.customers",
      icon: "users",
      path: "customers",
    };

    if (this.permissions.hasPermission(this.permissions.allPermissions.CUSTOMER_OVERVIEW_READ)) {
      return customers;
    } else {
      return;
    }
  }

  /**
   * Builds the notifications item
   * @returns the built item or undefined if the user has no permission
   */
  private buildNotifications(): SideBarMenuItem | undefined {
    const notifications: SideBarMenuItem = {
      name: "side_bar.notifications",
      icon: "notifications",
      notifications: true,
      path: "notifications",
    };

    if (this.permissions.hasPermission(this.permissions.allPermissions.NOTIFICATION_READ)) {
      return notifications;
    } else {
      return;
    }
  }

  /**
   * Builds the orders item
   * @returns the built item or undefined if the user has no permission
   */
  private buildOrders(): SideBarMenuItem | undefined {
    const orders: SideBarMenuItem = {
      name: "side_bar.orders",
      icon: "service",
      children: [],
      showChildren: false,
    };

    if (this.permissions.hasPermission(this.permissions.allPermissions.REPAIR_REQUEST_UPDATE)) {
      orders.children?.push({ name: "side_bar.repair", path: "repairs" });
    }
    if (
      this.permissions.hasPermission(
        this.permissions.allPermissions.EXT_SERVICE_CONTRACT_READ ||
          this.permissions.allPermissions.INT_SERVICE_CONTRACT_READ
      )
    ) {
      orders.children?.push({
        name: "side_bar.service",
        path: "serviceOrders",
      });
    }

    // return only if children is not empty:
    if (orders.children?.length !== undefined && orders.children?.length > 0) {
      return orders;
    } else {
      return;
    }
  }

  /**
   * Builds the fault item
   * @returns the built item or undefined if the user has no permission
   */
  private buildFault(): SideBarMenuItem | undefined {
    const fault: SideBarMenuItem = {
      name: "side_bar.fault",
      icon: "fault",
      children: [],
      showChildren: false,
    };

    if (this.permissions.hasPermission(this.permissions.allPermissions.FAULT_WIKI_READ)) {
      fault.children?.push({ name: "side_bar.wiki", path: "wiki" });
    }
    if (this.permissions.hasPermission(this.permissions.allPermissions.FAULT_REPORTING_READ)) {
      fault.children?.push({
        name: "side_bar.fault_reporting",
        path: "faultReporting",
      });
    }
    if (
      this.permissions.hasPermission(this.permissions.allPermissions.FAULT_LIST_READ) &&
      this.permissions.hasPermission(this.permissions.allPermissions.FAULT_LIST_MAINT_READ)
    ) {
      fault.children?.push({
        name: "side_bar.fault_allocation",
        path: "faultAllocation",
      });
    }
    if (this.permissions.hasPermission(this.permissions.allPermissions.IGNORED_FAULT_LIST_READ)) {
      fault.children?.push({
        name: "side_bar.ignored_fault_list",
        path: "faultIgnore",
      });
    }
    // return only if children is not empty:
    if (fault.children?.length !== undefined && fault.children?.length > 0) {
      return fault;
    } else {
      return;
    }
  }

  /**
   * Builds the map item
   * @returns the built item or undefined if the user has no permission
   */
  private buildMap(): SideBarMenuItem | undefined {
    const map: SideBarMenuItem = {
      name: "side_bar.map",
      icon: "location",
      path: "map",
    };

    if (this.permissions.hasPermission(this.permissions.allPermissions.MAP_READ)) {
      return map;
    } else {
      return;
    }
  }

  /**
   * Builds the gate systems item
   * @returns the built item or undefined if the user has no permission
   */
  private buildGateSystems(): SideBarMenuItem | undefined {
    const gateSystems: SideBarMenuItem = {
      name: "side_bar.gate_systems",
      icon: "administration",
      children: [],
      showChildren: false,
    };

    // Build the subitems based on permissions:
    if (
      this.permissions.hasPermission(
        this.permissions.allPermissions.ASSET_MASTER_DATA_READ ||
          this.permissions.allPermissions.ASSET_DYNAMIC_DATA_READ
      )
    ) {
      gateSystems.children?.push({
        name: "side_bar.manage",
        path: "assetAdministration",
      });
    }
    if (
      this.permissions.hasPermission(this.permissions.allPermissions.ASSET_COMPONENT_CREATE) &&
      this.permissions.hasPermission(this.permissions.allPermissions.ASSET_COMPONENT_READ)
    ) {
      gateSystems.children?.push({
        name: "side_bar.gate_components",
        path: "gateComponents",
      });
    }
    if (this.permissions.hasPermission(this.permissions.allPermissions.ASSET_STATUS_DETAILS_READ)) {
      gateSystems.children?.push({ name: "side_bar.status", path: "status" });
    }
    if (this.permissions.hasPermission(this.permissions.allPermissions.STATISTICS_READ)) {
      gateSystems.children?.push({
        name: "side_bar.statistics",
        path: "statistics",
      });
    }

    // return only if children is not empty:
    if (gateSystems.children?.length !== undefined && gateSystems.children?.length > 0) {
      return gateSystems;
    } else {
      return;
    }
  }
}
